import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, RentmanIntegrationSettings as RentmanIntegrationSettingsType } from '@/types';
import { apiService } from '@/utils/api';

const RentmanIntegration: React.FC = () => {
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<RentmanIntegrationSettingsType>>({
    invoice_create_latest_poll_time: ''
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const queryClient = useQueryClient();

  // Get sites
  const { data: sites = [] } = useQuery<Site[]>('sites', apiService.getSites);

  // Get Rentman integration settings for selected site
  const { data: integrationSettings, isLoading } = useQuery<RentmanIntegrationSettingsType>(
    ['rentmanIntegrationSettings', selectedSiteId],
    () => apiService.getRentmanIntegrationSettings(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
    }
  );

  // Handle form data population when integrationSettings changes
  useEffect(() => {
    if (selectedSiteId && integrationSettings !== undefined) {
      // Only set form data if we have actual integration settings (not empty/null)
      if (integrationSettings && integrationSettings.id !== undefined && integrationSettings.id !== null) {
        setFormData({
          invoice_create_latest_poll_time: integrationSettings.invoice_create_latest_poll_time || ''
        });
      } else {
        // No existing data, set empty form for POST request
        setFormData({
          invoice_create_latest_poll_time: ''
        });
      }
    }
  }, [integrationSettings, selectedSiteId]);

  // Set first site as default
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  // Save mutation
  const saveMutation = useMutation(
    (data: RentmanIntegrationSettingsType) => {
      if (integrationSettings && integrationSettings.id !== undefined && integrationSettings.id !== null) {
        return apiService.updateRentmanIntegrationSettings(selectedSiteId!, data);
      } else {
        return apiService.createRentmanIntegrationSettings(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['rentmanIntegrationSettings', selectedSiteId]);
        setMessage({ type: 'success', text: 'Rentman Integration Settings saved' });
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => apiService.deleteRentmanIntegrationSettings(selectedSiteId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['rentmanIntegrationSettings', selectedSiteId]);
        setFormData({ invoice_create_latest_poll_time: '' });
        setMessage({ type: 'success', text: 'Rentman Integration Settings deleted' });
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      }
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData: RentmanIntegrationSettingsType = {
      ...formData,
      site_id: selectedSiteId!
    } as RentmanIntegrationSettingsType;

    // Remove empty timestamps from payload
    if (!submitData.invoice_create_latest_poll_time) {
      delete submitData.invoice_create_latest_poll_time;
    }

    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete these settings?')) {
      deleteMutation.mutate();
    }
  };


  // Convert datetime-local value to API format (YYYY-MM-DD H:i:s)
  const formatDateTimeForAPI = (value: string): string => {
    if (!value) return '';
    const date = new Date(value);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Convert API datetime format to datetime-local input format
  const formatDateTimeForInput = (value: string): string => {
    if (!value) return '';
    const date = new Date(value);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      invoice_create_latest_poll_time: formatDateTimeForAPI(value)
    }));
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Rentman Integration Settings</h1>

      {/* Messages */}
      {message && (
        <div className={`mb-4 p-4 rounded ${
          message.type === 'success' ? 'bg-green-100 text-green-800' :
          message.type === 'error' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {message.text}
        </div>
      )}

      {isLoading && (
        <div className="mb-4 p-4 bg-blue-100 text-blue-800 rounded">
          Loading...
        </div>
      )}

      {/* Main Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="site_id" className="block text-sm font-medium text-gray-700 mb-2">
              Site ID
            </label>
            <select
              id="site_id"
              name="site_id"
              value={selectedSiteId || ''}
              onChange={(e) => setSelectedSiteId(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select a site</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="invoice_create_latest_poll_time" className="block text-sm font-medium text-gray-700 mb-2">
              Latest time Update Products Woo→Rentman
            </label>
            <input
              type="datetime-local"
              id="invoice_create_latest_poll_time"
              name="invoice_create_latest_poll_time"
              value={formatDateTimeForInput(formData.invoice_create_latest_poll_time || '')}
              onChange={handleDateTimeChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              disabled={saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save'}
            </button>

            {integrationSettings && integrationSettings.id !== undefined && integrationSettings.id !== null && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                disabled={deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default RentmanIntegration;
